import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CategoryEditPopover from '../../components/Inventory/CategoryEditPopover';
import StyledDataGrid from 'components/DataGridTable';
import { confirm } from '../../components/MenuNew/ConfirmModal';
import {
  getCategoriesByMenu,
  deleteCategory,
} from '../../redux/actions/inventory/inventory';
import { selectCategoriesByMenu } from '../../redux/selectors';

const InventoryCategories = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Get categories data from Redux store
  const categoriesByMenuArray = useSelector(selectCategoriesByMenu);

  // Fetch categories from menu if available
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(getCategoriesByMenu());
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Use categoriesByMenu array directly
  const dataSource = categoriesByMenuArray || [];

  const filteredCategories = useMemo(() => {
    if (!search) return dataSource;
    const q = search.toLowerCase();
    return dataSource.filter((c) => c.name?.toLowerCase().includes(q));
  }, [search, dataSource]);

  const handleEdit = (category) => {
    history.push(`/inventory/categories/${category._id}`);
  };

  const handleDelete = async (category) => {
    const result = await confirm({
      title: t('Confirm Category Deletion'),
      message: t('Are you sure you want to delete this category?'),
      confirmText: t('delete'),
      cancelText: t('cancel'),
      background: '#E21508',
    });

    if (result === true) {
      try {
        setLoading(true);
        await dispatch(deleteCategory(category._id || category.id));
        // Refresh the list
        await dispatch(getCategoriesByMenu());
      } catch (error) {
        console.error('Error deleting category:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleQuickEdit = (event, category) => {
    setSelectedCategory(category);
    setPopoverAnchor(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSelectedCategory(null);
  };

  const handleSaveCategory = async () => {
    await dispatch(getCategoriesByMenu());
  };

  const handleAddNew = () => {
    history.push('/inventory/categories/new');
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Category',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="Click to quick edit" arrow>
          <Typography
            sx={{ cursor: 'pointer', color: 'black' }}
            onClick={(e) => handleQuickEdit(e, params.row)}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    { field: 'itemsCount', headerName: 'Items', width: 120 },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, backgroundColor: 'white', minHeight: '100vh' }}>
      {/* Search and Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <TextField
          size="small"
          placeholder={t('Search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: '350px', flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleAddNew}
            startIcon={<AddIcon />}
          >
            {t('ADD NEW')}
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
        <StyledDataGrid
          rows={filteredCategories}
          columns={columns}
          getRowId={(row) => row._id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableSelectionOnClick
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          hideToolbar={true}
        />
      </Box>

      {/* Category Edit Popover */}
      <CategoryEditPopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        category={selectedCategory}
        onSave={handleSaveCategory}
      />
    </Box>
  );
};

export default InventoryCategories;
